import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { push } from "react-router-redirect";
import {
  displayActionableToast,
  displayToast,
} from "../../components/app/AppToast";
import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { useApiQuery, useApiRevalidate, type Api } from "../../services/api";
import {
  gaEventVisualAbstractGenerationFailed,
  gaEventVisualAbstractGenerationSucceeded,
} from "../../tools/analytics/domains/article.analytics";
import {
  dismissFailedVisualAbstract,
  readFailedVisualAbstracts,
  readWatchedVisualAbstracts,
  recordFailedVisualAbstract,
  unwatchVisualAbstract,
  useFailedVisualAbstracts,
  useWatchedVisualAbstracts,
  VISUAL_ABSTRACT_WATCH_TTL_MS,
} from "./visualAbstractWatchStore";

type VisualAbstractStatuses =
  Api["get"]["/webapp/private/visual-abstracts"]["output"];

// Slower than the card's own 3s poll: this one runs anywhere in the app, and
// nothing on screen is waiting on it. It costs one row lookup per tick and
// stops entirely the moment every watched run has settled.
const WATCH_POLL_INTERVAL_MS = 5_000;

const previewPath = (id: string): string => `/visual-abstract/${id}/preview`;

const LIBRARY_PATH = "/visual-abstracts";

/**
 * Announces a visual abstract finishing, wherever the user happens to be.
 *
 * Generation is a background Inngest job that runs for tens of seconds, so the
 * page that launched it is not where it lands. Mounted once in the app shell,
 * this watches the runs this device started (see `visualAbstractWatchStore`)
 * and raises an actionable toast on the transition to a terminal state.
 *
 * It also owns the outcome analytics — previously emitted by the chat card,
 * which could only count the users who stood and waited. There is exactly one
 * emitter so that moving does not double-count.
 */
export function useVisualAbstractWatcher(): void {
  const { t } = useTranslation();
  const location = useLocation();
  const revalidate = useApiRevalidate();
  const watched = useWatchedVisualAbstracts();
  const failed = useFailedVisualAbstracts();
  const ids = watched.map((entry) => entry.id);

  const refetchInterval = useCallback(
    (data: VisualAbstractStatuses | undefined) =>
      data !== undefined &&
      data.body.artifacts.some(
        (artifact) =>
          artifact.status === "queued" || artifact.status === "generating"
      )
        ? WATCH_POLL_INTERVAL_MS
        : false,
    []
  );

  const statusQuery = useApiQuery(
    "/webapp/private/visual-abstracts",
    ids.length === 0 ? false : { query: { ids }, refetchInterval, staleTime: 0 }
  );
  const artifacts =
    statusQuery.state === "success" ? statusQuery.body.artifacts : null;

  // The current path is read at settle time rather than captured, so a toast is
  // only suppressed when the user is looking at THAT poster right now.
  const pathname = location.pathname;

  // Expiry is swept on its own, at mount and whenever the list moves, rather
  // than inside the settle pass below: react-query's structural sharing keeps
  // the same `artifacts` reference while a run's status does not change, so a
  // job that never settles would never re-run that effect — and its entry would
  // poll forever on the one device that asked for it. The failed list shares
  // the TTL, so a retry card does not outlive the day it was relevant.
  useEffect(() => {
    const now = Date.now();
    for (const entry of readWatchedVisualAbstracts())
      if (
        now - new Date(entry.watchedAt).getTime() >=
        VISUAL_ABSTRACT_WATCH_TTL_MS
      )
        unwatchVisualAbstract(entry.id);
    for (const entry of readFailedVisualAbstracts())
      if (
        now - new Date(entry.failedAt).getTime() >=
        VISUAL_ABSTRACT_WATCH_TTL_MS
      )
        dismissFailedVisualAbstract(entry);
  }, [watched, failed]);

  useEffect(() => {
    if (artifacts === null) return;
    const now = Date.now();

    for (const entry of readWatchedVisualAbstracts()) {
      const artifact = artifacts.find((candidate) => candidate.id === entry.id);
      // Absent means the artifact was deleted, or the response predates this
      // entry being added. Neither is an outcome; the TTL collects it.
      if (artifact === undefined) continue;
      if (artifact.status === "queued" || artifact.status === "generating")
        continue;

      unwatchVisualAbstract(artifact.id);

      const dimensions = {
        visualAbstractId: artifact.id,
        articleId: entry.articleId,
        roomId: artifact.roomId,
        language: artifact.language,
        family: artifact.family,
        sourceType: artifact.sourceType,
        generatorVersion: artifact.generatorVersion,
        latencyMs: now - new Date(artifact.createdAt).getTime(),
      };
      const openPreview = (): void => push(previewPath(artifact.id));
      const alreadyOnPreview = pathname === previewPath(artifact.id);

      // The paper the poster is of, on the toast's second line. The reader can
      // have several runs going, and "your visual abstract is ready" alone does
      // not say which one just landed.
      switch (artifact.status) {
        case "ready":
          gaEventVisualAbstractGenerationSucceeded({
            ...dimensions,
            blockers: artifact.blockers,
          });
          // The library lists ready posters only and shows this run as a
          // placeholder until now. Unwatching removed the placeholder; the
          // refetch puts the poster in its place instead of an empty slot.
          void revalidate("/webapp/private/visual-abstracts/library");
          // A poster of the pair supersedes an earlier failure of it, however
          // this run was started.
          dismissFailedVisualAbstract({
            articleId: entry.articleId,
            roomId: artifact.roomId,
          });
          if (!alreadyOnPreview)
            displayActionableToast(
              {
                title: t("Your visual abstract is ready"),
                description: entry.articleTitle,
              },
              "visual_abstract",
              openPreview
            );
          continue;
        case "failed":
          gaEventVisualAbstractGenerationFailed({
            ...dimensions,
            failureCode: artifact.failure.code,
          });
          // The desktop library holds a retry card for this run, so the toast
          // sends the reader there rather than to a preview of a poster that
          // does not exist. On the library itself the card names the paper.
          // The shell is the user agent's, so a failure recorded here is only
          // ever read by a desktop library.
          if (isDesktopShell()) {
            recordFailedVisualAbstract({
              id: artifact.id,
              articleId: entry.articleId,
              articleTitle: entry.articleTitle,
              roomId: artifact.roomId,
            });
            const title = t(
              "Visual abstract not generated. Try again from the card."
            );
            // The preview of this run already says it failed.
            if (alreadyOnPreview) continue;
            if (pathname === LIBRARY_PATH) displayToast(title, "error");
            else
              displayActionableToast(
                { title, description: entry.articleTitle },
                "error",
                () => push(LIBRARY_PATH)
              );
            continue;
          }
          if (!alreadyOnPreview)
            displayActionableToast(
              {
                title: t("Visual abstract generation failed"),
                description: entry.articleTitle,
              },
              "error",
              openPreview
            );
          continue;
        default:
          throw new Error(artifact satisfies never);
      }
    }
  }, [artifacts, pathname, revalidate, t]);
}
